import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import Axios from 'axios';
import { injectStateService, StateService } from '@/shared/services/StateService';
import AuthService from '@/shared/services/AuthService';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import Button from '@/shared/components/Button';
import { AddIcon } from '@/shared/components/Icons';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { CanCreate } from '@/shared/components/security';
import DashboardTimeTile from './partials/DashboardTimeTile';
import moment from 'moment';
import FillGapsRegistrationModal from '@/shared/components/registration/FillGapsRegistrationModal';

import messages from '../dashboardMessages';

const MyRegistrationsDashboard = ({ intl, stateService }) => {
  const [data, setData] = useState(null);
  const [showRegistrationModal, setRegistrationModal] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [showFillGapsRegistrationModal, setShowFillGapsRegistrationModal] = useState(null);

  useEffect(() => {
    const cancelToken = Axios.CancelToken.source();

    api.base
      .get(`${config.appUrls.api}/api/registration/MyDashboard/`, {
        cancelToken: cancelToken.token,
      })
      .then(response => {
        setData(response.data || {});
      }, handleHttpError(intl));
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [intl, reloadCount]);

  const openRegOverview = useCallback(
    period => {
      const currentUserId = AuthService.getUserId();
      stateService.gotoRegistrationOverview({ period, userId: currentUserId });
    },
    [stateService],
  );

  const openFillGapsRegistrationModal = (argDateFrom, argDateUntil) => {
    const userId = AuthService.getUserId();
    const remainingOvertime = data.overtimeRemaining;
    let dateFrom = moment.utc(argDateFrom);
    let dateUntil = moment.utc(argDateUntil);

    const registration = { userId };
    // If there is remaining overtime, set the registration type to recup
    if (remainingOvertime !== null && remainingOvertime > 0) registration.type = 4;
    setShowFillGapsRegistrationModal({
      registration,
      dateFrom,
      dateUntil,
      userId,
      remainingOvertime,
    });
  };

  return (
    <div className="my-registrations-dashboard">
      <div className="title-block">
        <FormattedMessage {...messages.MyRegistrations} />
      </div>
      <div className="content-block">
        {!data ? (
          <LoadingAnimation />
        ) : (
          <div className="tile-container">
            <div className="tile-group">
              <DashboardTimeTile
                title={messages.Today}
                value={data.today}
                overtime={data.overtimeToday}
                missing={data.missingToday}
                onClick={() => openRegOverview('today')}
                onFillGaps={() =>
                  openFillGapsRegistrationModal(
                    moment.utc().startOf('day'),
                    moment.utc().startOf('day'),
                  )
                }
              />
              <DashboardTimeTile
                title={messages.Yesterday}
                value={data.yesterday}
                overtime={data.overtimeYesterday}
                missing={data.missingYesterday}
                onClick={() => openRegOverview('yesterday')}
                onFillGaps={() =>
                  openFillGapsRegistrationModal(
                    moment
                      .utc()
                      .startOf('day')
                      .subtract(1, 'days'),
                    moment
                      .utc()
                      .startOf('day')
                      .subtract(1, 'days'),
                  )
                }
              />
            </div>
            <div className="tile-group">
              <DashboardTimeTile
                title={messages.ThisWeek}
                value={data.thisWeek}
                overtime={data.overtimeThisWeek}
                missing={data.missingThisWeek}
                onClick={() => openRegOverview('week')}
                onFillGaps={() =>
                  openFillGapsRegistrationModal(
                    moment.utc().startOf('week'),
                    moment.utc().startOf('day'),
                  )
                }
              />
              <DashboardTimeTile
                title={messages.ThisMonth}
                value={data.thisMonth}
                overtime={data.overtimeThisMonth}
                missing={data.missingThisMonth}
                onClick={() => openRegOverview('month')}
                onFillGaps={() =>
                  openFillGapsRegistrationModal(
                    moment.utc().startOf('month'),
                    moment.utc().startOf('day'),
                  )
                }
              />
            </div>
          </div>
        )}
      </div>
      <CanCreate on="RegistrationOwn">
        <div className="bottom-block">
          <Button btnClassName="btn btn-sm btn-inverse" onClick={() => setRegistrationModal(true)}>
            <AddIcon />
            <span>{intl.formatMessage(messages.NewRegistration)}</span>
          </Button>
          <div className="pull-right">
            {data && data.overtimeRemaining ? (
              <span className="pull-right">
                <FormattedMessage {...messages.RemainingOvertimeHours} />
                :&nbsp;
                {data.overtimeRemaining.minuteToTime()}
              </span>
            ) : null}
            <br />
          </div>
        </div>
      </CanCreate>
      {showRegistrationModal ? (
        <RegistrationModal
          show
          onSave={() => {
            setReloadCount(reloadCount + 1); // Force reload
            setRegistrationModal(false);
          }}
          onClose={() => setRegistrationModal(false)}
          editableRelation
        />
      ) : null}
      {showFillGapsRegistrationModal && (
        <FillGapsRegistrationModal
          {...showFillGapsRegistrationModal}
          show
          onSave={() => {
            setShowFillGapsRegistrationModal(null);
            setReloadCount(reloadCount + 1);
          }}
          onClose={() => setShowFillGapsRegistrationModal(null)}
        />
      )}
    </div>
  );
};
MyRegistrationsDashboard.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(MyRegistrationsDashboard));
